import {Close, Edit} from '@material-ui/icons';
import {FormattedMessage, useIntl} from 'react-intl';
import React, {useCallback, useMemo, useState, useRef} from 'react';

import {StudioSearch} from '../../../base-components/StudioSearch';
import {StudioPopover} from '../../../base-components/StudioPopover';
import {StudioTextField} from '../../../base-components/StudioTextField';
import FormButton from '../../../base-components/StudioButton/FormButton';
import {CategoryType} from '../../../types/dataset/DatasetGetAnnotationsResponse';

import './LabelReview.scss';

const INPUT_OFFSET_PX = 40;

interface LabelReviewProps {
  labels: CategoryType[];
  onAddMoreClick: () => void;
  onChange: (labels: CategoryType[]) => void;
}

export const LabelReview = ({labels, onChange, onAddMoreClick}: LabelReviewProps) => {
  const intl = useIntl();
  const [search, setSearch] = useState('');
  const [editingLabelWidth, setEditingLabelWidth] = useState<number>(0);
  const [editingLabelValue, setEditingLabelValue] = useState<string>('');
  const [editingLabelId, setEditingLabelId] = useState<number | null>(null);
  const labelsRefHash = useRef<{[key: number]: HTMLSpanElement | null}>({});

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  }, []);

  const filteredLabels = useMemo(
    () =>
      labels.filter(label => label.name.toUpperCase().startsWith(search.toUpperCase())),
    [labels, search]
  );

  const groups = useMemo(
    () =>
      filteredLabels.reduce((finalGroups, label) => {
        const group = label.name.charAt(0).toUpperCase();

        if (!finalGroups[group]) {
          finalGroups[group] = [];
        }
        finalGroups[group].push(label);

        return finalGroups;
      }, {} as Record<string, CategoryType[]>),
    [filteredLabels]
  );

  const handleLabelEdit = useCallback((label: CategoryType) => {
    const labelElement = labelsRefHash.current[label.id];

    if (labelElement) {
      const width = labelElement.offsetWidth;

      setEditingLabelWidth(width + INPUT_OFFSET_PX);
    }

    setEditingLabelId(label.id);
    setEditingLabelValue(label.name);
  }, []);

  const handleBlur = useCallback(
    (labelToEdit: CategoryType) => {
      if (labelToEdit.name !== editingLabelValue && editingLabelValue.trim() !== '') {
        const updatedLabels = labels.map(label =>
          labelToEdit.id === label.id ? {...label, name: editingLabelValue.trim()} : label
        );

        onChange(updatedLabels);
      }

      setEditingLabelId(null);
      setEditingLabelWidth(0);
      setEditingLabelValue('');
    },
    [editingLabelValue, labels, onChange]
  );

  const handleLabelDelete = useCallback(
    (removedLabel: CategoryType) => {
      const updatedLabels = labels.filter(label => label.id !== removedLabel.id);

      onChange(updatedLabels);
    },
    [labels, onChange]
  );

  const setLabelRef = useCallback((labelId: number, element: HTMLSpanElement | null) => {
    labelsRefHash.current[labelId] = element;
  }, []);

  return (
    <div className="label-review">
      <div className="label-review__menu">
        <div className="label-review__count">
          <FormattedMessage
            tagName="span"
            id="dataPrep.labelReview.labelsCount"
            values={{labelsCount: filteredLabels.length}}
          />
          <StudioPopover
            ttPlacement="right"
            infoIconClass="label-review__tooltip"
            infoMessage={intl.formatMessage({id: 'dataPrep.labelReview.tooltip'})}
          />
        </div>
        <div className="label-review__btn-group">
          <StudioSearch
            value={search}
            onChange={handleSearchChange}
            placeholder={intl.formatMessage({
              id: 'dataPrep.labelReview.searchPlaceholder',
            })}
          />
          <FormButton
            buttonRole="secondary"
            onClick={onAddMoreClick}
            value={intl.formatMessage({id: 'dataPrep.labelReview.addMore'})}
          />
        </div>
      </div>

      <ul className="label-review__editor">
        {Object.entries(groups).length ? (
          Object.entries(groups)
            .sort(([group1], [group2]) => group1.localeCompare(group2))
            .map(([group, groupLabels], index) => (
              <li className="label-review__group" key={`${group}-${index}`}>
                <h4 className="label-review__group-header">
                  <span>{group}</span>
                </h4>
                <ul className="label-list">
                  {groupLabels
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(label => (
                      <li key={label.id} className="label-list__chip">
                        {editingLabelId === label.id ? (
                          <StudioTextField
                            autoFocus
                            value={editingLabelValue}
                            className="label-list__input"
                            style={{width: editingLabelWidth}}
                            onBlur={() => handleBlur(label)}
                            onChange={event => setEditingLabelValue(event.target.value)}
                          />
                        ) : (
                          <span
                            className="label-list__name"
                            ref={element => setLabelRef(label.id, element)}
                          >
                            {label.name}
                          </span>
                        )}
                        <div
                          role="button"
                          className="label-list__edit"
                          onClick={() => handleLabelEdit(label)}
                        >
                          <Edit />
                        </div>
                        <div
                          role="button"
                          className="label-list__delete"
                          onClick={() => handleLabelDelete(label)}
                        >
                          <Close />
                        </div>
                      </li>
                    ))}
                </ul>
              </li>
            ))
        ) : (
          <div className="label-review__empty">
            <FormattedMessage tagName="span" id="dataPrep.labelReview.noLabelsFound" />
          </div>
        )}
      </ul>
    </div>
  );
};
